import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, Container, Dialog, DialogContent, DialogTitle, Grid, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import BuildingTree from '../../lib/components/BuildingTree';
import RoomCard from '../../lib/components/RoomCard';
import RoomForm from '../../lib/components/RoomForm';
import { useSchoolStore } from '../../stores/schoolStore';
import { Room } from '../../types';

export const SchoolStructure: React.FC = () => {
  const { buildings, floors, rooms, addRoom, updateRoom } = useSchoolStore();
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);

  const handleRoomSelect = (room: Room) => {
    setSelectedRoom(room);
  };

  const handleAddRoom = () => {
    setEditingRoom(null);
    setIsFormOpen(true);
  };

  const handleEditRoom = () => {
    if (selectedRoom) {
      setEditingRoom(selectedRoom);
      setIsFormOpen(true);
    }
  };

  const handleFormSubmit = (data: any) => {
    if (editingRoom) {
      updateRoom(editingRoom.id, {
        ...data,
        usage: {
          hasFixedTeacher: data.hasFixedTeacher,
          isShared: data.isShared,
          isNomadic: data.isNomadic,
        },
      });
    } else {
      const newRoom: Room = {
        id: `room-${Date.now()}`,
        floorId: data.floorId,
        name: data.name,
        roomNumber: data.roomNumber,
        roomType: data.roomType,
        capacity: data.capacity,
        resources: [],
        accessibility: data.accessibility,
        usage: {
          hasFixedTeacher: data.hasFixedTeacher,
          isShared: data.isShared,
          isNomadic: data.isNomadic,
        },
      };
      addRoom(newRoom);
    }
    setIsFormOpen(false);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          School Structure
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddRoom}>
          Add Room
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Buildings & Rooms
            </Typography>
            <BuildingTree
              buildings={buildings}
              floors={floors}
              rooms={rooms}
              onRoomSelect={handleRoomSelect}
              selectedRoomId={selectedRoom?.id}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedRoom ? (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Room Details</Typography>
                <Button variant="outlined" onClick={handleEditRoom}>
                  Edit Room
                </Button>
              </Box>
              <RoomCard room={selectedRoom} />
            </Paper>
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                height: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Select a room from the tree to view details
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add New Room'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <RoomForm
              room={editingRoom || undefined}
              floorId={editingRoom?.floorId || floors[0]?.id || ''}
              onSubmit={handleFormSubmit}
              onCancel={() => setIsFormOpen(false)}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default SchoolStructure;
